import { stampSignupFlag } from '@common-components/ext-trial/ext-signup-flag';
import { accessFormPopupActions } from '@common-reducers/AccessFormPopupReducer';
import { authNavigationTarget, preserveAuthRedirect } from '../../../utils/auth-redirect';
import { mainSliceActions } from "@common-reducers/MainReducer";
import { clearAuthError, setAuthError, setSignupState } from "@common-reducers/UserReducer";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { useRouter } from "@common-reducers/hooks/use-router";
import { isExtensionContext } from "@common-services/detect-context";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { namespace } from "@common-config/constants";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { authService, SignupData } from "@/common/bt-auth/auth-service";
import GoogleLoginWrapper from "../google-auth-button/google-login-wrapper";
import { Mail, Lock } from 'lucide-react';
import { cn } from "@/lib/utils";
import { getRuntimeUrl } from '@common-services/storage';
import { flowPath, isClearFlow } from '@common/utils/clear-flow';
import { CLEAR_BRAND_LOGO, brandName } from '@common/config/clear-brand';

export const SignupForm = () => {
  const { t, i18n } = useTranslation(namespace);
  const analyticsService = useAnalyticsService();
  const dispatch = useAppDispatch();
  
  const validateSignupData = authService.validateSignupData.bind(authService);
  const validateEmail = authService.validateEmail.bind(authService);
  const authError = useAppSelector((state) => state.UserReducer.authError);
  const NewSignup = useAppSelector((state) => state.UserReducer.signup);
  const [showValidation, setShowValidation] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [accountExists, setAccountExists] = useState(false);

  const router = useRouter();
  const isRegistering = useAppSelector((state) => state.UserReducer.authenticationStatus) === 'userRegisterStarted';

  const { email, password } = NewSignup;

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    
    if (name === 'email') {
      const trimmedEmail = value.trim();
      if (trimmedEmail) {
        if (validateEmail(trimmedEmail)) {
          setEmailError('');
          setShowValidation(false);
        } else {
          setEmailError('Invalid email address');
          setShowValidation(true);
        }
      } else {
        setEmailError('');
        setShowValidation(false);
      }
    }

    dispatch(setSignupState({
      ...NewSignup,
      [name]: value
    }));
    dispatch(clearAuthError());
    setAccountExists(false);
  };

  const handleSignup = async () => {
    const trimmedEmail = (email ?? '').trim();

    // Prepare signup data
    const signupData: SignupData = {
      email: trimmedEmail.toLowerCase(),
      password: password ?? '',
    };

    // Validate form data
    const validation = validateSignupData(signupData);
    if (!validation.isValid) {
      setShowValidation(true);
      setEmailError(validation.errors.find(e => e.includes('email')) || '');
      // Email errors are shown inline via emailError, and an empty password is
      // shown inline via the field message. Only surface through authError the
      // one case inline can't render: a filled-in but too-short password
      // (avoids a duplicate "required" message under the password field).
      const passwordFilledButTooShort = !!password && password.length < 6;
      dispatch(
        passwordFilledButTooShort
          ? setAuthError('Password must be at least 6 characters long')
          : clearAuthError()
      );
      return;
    }

    try {
      setAccountExists(false);
      const authSearch = window.location.search;
      const result = await authService.signup(signupData, dispatch);

      if (result.success) {
        // NOTE: register_new_user / sign_up is now fired ONCE server-side for ALL
        // signups (incl. web email) by the backend track-attribution hook via
        // Measurement Protocol, joining the GA4 session through the stored
        // ga_client_id. The previous client-side emit here double-fired with the
        // server (the 1.26x multi-fire diagnosed in Project B) and was removed.

        if (isExtensionContext()) {
          dispatch(accessFormPopupActions.markSignedUp());
          stampSignupFlag();
        }
        if (result.redirectTo && !isExtensionContext()) {
          // Redirect to the specified path for new signups
          router.navigate(authNavigationTarget(authSearch, result.redirectTo));
        }
      } else if (result.code === 'account_exists') {
        setAccountExists(true);
      } else if (result.error) {
        dispatch(setAuthError(result.error));
      }
    } catch (error) {
      console.error('Signup error:', error);
      dispatch(setAuthError('An unexpected error occurred'));
    }
  };

  function handleFormState(target: string) {
    if (target === '') {
      analyticsService.event("switch_to_log_in", {
        category: "access",
        action: "switch to log in",
      });
    }
    dispatch(mainSliceActions.setFormState(target));
    if (!isExtensionContext()) {
      router.navigate(preserveAuthRedirect(flowPath('/login'), window.location.search));
    }
  }

  const errorFormColor = '#f72371';

  useEffect(() => {
    document.title = t('signupForm.title');
    let userEmail = new URLSearchParams(window.location.search).get('email');
    if (userEmail) {
      const safeEmail = userEmail.replace(/ /g, '+');
      dispatch(setSignupState({
        email: safeEmail,
        password: '',
      }));
    }
  }, []);

  return (
    <div className="p-6 space-y-6 max-w-lg mx-auto w-full">
      {/* Logo and Title */}
      <div className="flex flex-col items-center space-y-4">
        <img
          src={isClearFlow() ? CLEAR_BRAND_LOGO : getRuntimeUrl("/img/blueticks_logo_icon.png")}
          alt={`${brandName()} Logo`}
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
          }}
        />
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--color-text, #111827)' }}>{brandName()}</h2>
          <p className="text-sm" style={{ color: 'var(--color-text-muted, #6b7280)' }}>{t('signupForm.formTitle')}</p>
        </div>
      </div>

      {/* Form */}
      <form
        className="space-y-4"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSignup();
          }
        }}
      >
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium leading-none block mb-2" style={{ color: 'var(--color-text, #111827)' }}>
            {t('signupForm.emailLabel')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--color-text-muted, #6b7280)' }} />
            <input
              id="email"
              name="email"
              type="email"
              placeholder={t('signupForm.emailPlaceholder')}
              value={email}
              onChange={handleOnChange}
              className={cn(
                "bt-access-input flex h-10 w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
                (showValidation && !email) || emailError ? 'border-red-500' : ''
              )}
              style={{
                borderColor: (showValidation && !email) || emailError ? errorFormColor : 'var(--color-border, #e5e7eb)',
                backgroundColor: 'var(--color-surface, #ffffff)',
                color: 'var(--color-text, #111827)',
              } as React.CSSProperties}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 1px rgba(59, 130, 246, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = '';
              }}
            />
          </div>
          {((showValidation && !email) || emailError) && (
            <p className="text-sm text-red-500">
              {(showValidation && !email) ? t('signupForm.emailError') : emailError}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium leading-none block mb-2" style={{ color: 'var(--color-text, #111827)' }}>
            {t('signupForm.passwordLabel')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--color-text-muted, #6b7280)' }} />
            <input
              id="password"
              name="password"
              type="password"
              placeholder={t('signupForm.passwordPlaceholder')}
              value={password}
              onChange={handleOnChange}
              className={cn(
                "bt-access-input flex h-10 w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
                showValidation && !password ? 'border-red-500' : ''
              )}
              style={{
                borderColor: showValidation && !password ? errorFormColor : 'var(--color-border, #e5e7eb)',
                backgroundColor: 'var(--color-surface, #ffffff)',
                color: 'var(--color-text, #111827)',
              } as React.CSSProperties}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 1px rgba(59, 130, 246, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = '';
              }}
            />
          </div>
          {showValidation && !password && (
            <p className="text-sm text-red-500">{t('signupForm.passwordError')}</p>
          )}
        </div>

        {authError && (
          <p className="text-sm text-red-500" style={{ marginTop: '-8px', marginBottom: '8px' }}>{authError}</p>
        )}
        {accountExists && (
          <p role="alert" className="text-sm text-red-500" style={{ marginTop: '-8px', marginBottom: '8px' }}>
            {t('signupForm.accountExists')}{' '}
            <button type="button" className="font-medium underline" onClick={() => handleFormState("")}>
              {t('signupForm.loginLink')}
            </button>
          </p>
        )}

        <button
          type="button"
          onClick={handleSignup}
          disabled={isRegistering}
          className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-10 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'var(--color-primary, #3b82f6)',
            color: 'var(--color-primary-contrast, #ffffff)',
          }}
        >
          {isRegistering ? t('signupForm.signingUp') : t('signupForm.signupButton')}
        </button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" style={{ borderColor: 'var(--color-border, #e5e7eb)' }} />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2" style={{ backgroundColor: 'var(--color-surface, #ffffff)', color: 'var(--color-text-muted, #6b7280)' }}>
            {t('signupForm.continueWith')}
          </span>
        </div>
      </div>

      {/* Google Sign In */}
      <div className="flex justify-center">
        <GoogleLoginWrapper />
      </div>

      {/* Footer */}
      <div className="text-center text-sm">
        <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>
          {t('signupForm.existingAccount')} {' '}
        </span>
        <button
          type="button"
          className="font-medium hover:underline"
          onClick={() => handleFormState("")}
          style={{ color: 'var(--color-primary, #3b82f6)' }}
        >
          {t('signupForm.loginLink')}
        </button>
      </div>
    </div>
  );
};




